import clsx from 'clsx';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import { combineRefs } from '../../../common/functions/combine-refs';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { MakeRequired } from '@beesoft/common';
import EmptyFieldLabel from '../../labels/empty-field-label';

export interface TextAreaProps extends MakeRequired<DynamicFieldComponentProps<string>, 'name'> {
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  showCharacterCounter?: boolean;
}

export default function TextArea({
  name,
  label,
  rows,
  value,
  error,
  readOnly,
  required,
  onChange,
  onFocus,
  inputRef,
  placeholder,
  disabled,
  labelStyles,
  maxLength,
  showCharacterCounter = false,
}: TextAreaProps) {
  const [stateValue, setStateValue] = useState<string>('');

  useEffect(() => {
    setStateValue(value ?? '');
  }, [value]);

  const tagCreator = useTestTag(label || '');

  const onFocusHandler = () => onFocus?.();

  const onChangeHandler = ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = target;

    setStateValue(value);
    onChange?.(value);
  };

  // same style as the Input component
  const inputStyles = clsx(
    'custom-scroll odin-appearance-none odin-leading-4 odin-border odin-relative odin-block odin-w-full odin-py-1 odin-placeholder-gray-3 odin-text-sm focus:odin-outline-none',
    {
      'odin-bg-mono-1 odin-px-2': !readOnly,
      'odin-opacity-70 odin-border-gray-4 odin-cursor-default': readOnly,
      'odin-border-error': error,
      'odin-border-gray-4': !error && !readOnly,
      'odin-hidden': readOnly && (!stateValue || stateValue.length === 0),
      'odin-cursor-default odin-text-readonly': disabled,
    }
  );

  const combinedRef = combineRefs(inputRef, tagCreator('textarea'));

  return (
    <div>
      {label && name && (
        <div className="odin-flex">
          <FieldLabel name={name} label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <textarea
        id={name}
        ref={combinedRef}
        rows={rows}
        name={name}
        className={inputStyles}
        onChange={onChangeHandler}
        onFocus={onFocusHandler}
        value={stateValue}
        readOnly={readOnly}
        spellCheck={true}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
      />
      {showCharacterCounter && maxLength && (
        <div className="odin-text-xs odin-text-gray-3 odin-text-right odin-mt-1">
          {stateValue.length}/{maxLength}
        </div>
      )}
      {readOnly && (!stateValue || stateValue.length === 0) && <EmptyFieldLabel />}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
}
